//  Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import { Component, OnInit } from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {IIntegration, PartnerTypes} from '../../../../types/integrations';
import {AlertDialogComponent} from '../../../dialogs/alert/alert.component';
import {ActivatedRoute, Router} from '@angular/router';
import {D3eService} from '../../../../services/d3e-service';
import {HeaderService} from '../../../header.service';
import {NotificationService} from '../../../../services/notification-service';
import {MatDialog} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';
import { Observable } from 'rxjs';
import {SelectedOrgMethod} from '../../../../shared/selectedOrg';
import { Theme, ThemeService } from '../../../../services/theme.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {switchMap} from 'rxjs/operators';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-sentinel-one',
    templateUrl: './sentinel-one.component.html',
    styleUrls: ['./sentinel-one.component.scss'],
    standalone: false
})
export class SentinelOneComponent implements OnInit {
  private readonly PARTNER_ID = 7;
  formGroup: UntypedFormGroup;
  integration: IIntegration;
  integrationId: string = null;
  selectedOrgId: string;
  hasUserBeenSet = false;
  theme$: Observable<Theme>;

  constructor(
      private route: ActivatedRoute,
      private d3eService: D3eService,
      private router: Router,
      private headerService: HeaderService,
      private formBuilder: UntypedFormBuilder,
      private notification: NotificationService,
      public dialog: MatDialog,
      private translate: TranslateService,
      private themeService: ThemeService,
  ) {
    this.headerService.setIcon('mdi-sync');
      this.headerService.setContextHelp([
        'help.integrations.sentinel-one.title',
      ]);
    this.formGroup = this.formBuilder.group({
      api_url: ['', Validators.required],
      api_token: ['', Validators.required],
      lock_option: ['', Validators.required],
      admin_unlock: [{value: false, disabled: true}],
    });
    this.theme$ = this.themeService.getTheme();
  }

  ngOnInit(): void {

    this.headerService.orgSelectedChanged
      .pipe(untilDestroyed(this))
      .subscribe(value => {
        this.selectedOrgId = value;
        if ( this.hasUserBeenSet ) {
          // TODO: launch the dirty flag window
          this.router.navigateByUrl('/integrations');
        }
        this.hasUserBeenSet = true;
    });

    this.route.params.subscribe(params => {
      this.integrationId = params.id;
      if (this.integrationId !== 'new') {
        const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

        this.d3eService.get_integration(this.integrationId, selectedOrg)
            .pipe(untilDestroyed(this))
            .subscribe(integration => {
              if (integration.partner_id !== this.PARTNER_ID) {
                this.router.navigateByUrl('/integrations');
              }
              this.formGroup.controls['api_url'].setValue(integration.config.api_url);
              this.formGroup.controls['api_token'].setValue(integration.config.api_token);
              this.formGroup.controls['lock_option'].setValue(integration.config.lock_option);
              this.formGroup.controls['admin_unlock'].setValue(integration.config.admin_unlock);
              this.integration = integration;
            });
      }
    });
  }

  goToIntegrations() {
    this.router.navigateByUrl('/integrations');
  }

  cancel() {
    if (this.formGroup.touched) {
      const dialogRef = this.dialog.open(AlertDialogComponent, {
        width: '450px',
        data: {
          title: this.translate.instant('DISCARD_CHANGES'),
          content: this.translate.instant('DISCARD_CHANGES_CONTENT'),
          rejectBtnName: this.translate.instant('NO'),
          acceptBtnName: this.translate.instant('YES'),
          showRejectBtn: true,
        },
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.goToIntegrations();
        }
      });
    } else {
      this.goToIntegrations();
    }
  }

  reportSaveSuccess() {
    this.notification.showSuccess(this.translate.instant('CONFIG_SAVED'));
  }

  reportSaveError(error) {
    this.notification.showError(this.translate.instant('REPORT_SAVE_ERROR'));
  }

  save() {
    this.notification.showUpdate(this.translate.instant('CHANGES_BEING_SAVED'));
    const data = this.formGroup.value;
    if (this.integrationId === 'new') {
      const integration: IIntegration = {
        display_name: 'Sentinel One',
        enabled: false,
        partner_id: PartnerTypes.SENTINEL_ONE,
        config: {
          api_url: data.api_url,
          api_token: data.api_token,
          admin_unlock: data.admin_unlock,
          lock_option: data.lock_option,
        }
      };
      const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

      this.d3eService.add_integration(integration, selectedOrg)
        .subscribe(() => {
          this.reportSaveSuccess();
          this.goToIntegrations();
        }, this.reportSaveError.bind(this));
    } else {
      this.integration.config = {
        api_url: data.api_url,
        api_token: data.api_token,
        admin_unlock: data.admin_unlock || false,
        lock_option: data.lock_option,
      };
      const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

      this.d3eService.update_integration(this.integration, selectedOrg)
        .subscribe(() => {
          this.reportSaveSuccess();
          this.goToIntegrations();
        }, this.reportSaveError.bind(this));
    }
  }

  validate() {
    const data = this.formGroup.value;
    this.d3eService.validate_sentinel_one_config(data.api_url, data.api_token).pipe(
      switchMap(result => {
        let dialogConfig = {                data: {
            title: this.translate.instant('integrations.sentinel-one.validation-title'),
            content: this.translate.instant('integrations.sentinel-one.validation-failure'),
            acceptBtnName: this.translate.instant('ALERT_OK'),
            showRejectBtn: false,
          },
        };
        if (result.status == 200) {
          dialogConfig.data.content = this.translate.instant(
            'integrations.sentinel-one.validation-success',
            {agentCount: result.agentCount}
          );
        }
        return this.dialog.open(AlertDialogComponent, dialogConfig).afterClosed();
      })
    ).subscribe();
  }
}
